import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Card, Table } from '../components/ui.jsx';
import { PageHeader } from '../components/PageSkeleton.jsx';
import { api } from '../lib/api.js';
import {
  fmtStamp,
  monthLabel,
  monthOptions,
  recentMonths,
  runUsageJob,
  thisMonth,
} from '../lib/usage.js';
import {
  fmt,
  monthlySeries,
  monthsSummary,
  summaryCards,
} from '../lib/usageTable.js';
import { MonthlyBarChart } from '../components/MonthlyBarChart.jsx';
import { Select } from '../components/controls/Select.jsx';
import { useSessionState } from '../lib/useSessionState.js';
import { sanitizeMonth } from '../lib/sessionState.js';

export function SourcesCard({ sources, lastRun }) {
  const voice = sources.voice_calls;
  if (!voice) return null;
  const origin = [voice.service, voice.database, voice.table]
    .filter(Boolean)
    .join(' › ');
  return (
    <Card title="Data sources" className="billing-sources">
      <div className="billing-source">
        <div className="billing-source-head">
          <b>{voice.label}</b>
          <span
            className={`project-status-badge ${voice.configured ? 'status-active' : 'status-paused'}`}
          >
            {voice.configured ? 'Connected' : 'Not configured'}
          </span>
        </div>
        <dl className="billing-source-facts">
          <div>
            <dt>Source</dt>
            <dd>
              {origin}
              {voice.host && <small>{voice.host} · read-only</small>}
              <small>
                Names:{' '}
                {voice.names?.configured
                  ? [
                      voice.names.service,
                      voice.names.database,
                      voice.names.collection,
                    ]
                      .filter(Boolean)
                      .join(' › ')
                  : 'not configured — unlinked organizations show ids only'}
              </small>
            </dd>
          </div>
          <div>
            <dt>Collects</dt>
            <dd>
              Inbound and outbound calls, duration and tarification minutes, per
              platform organization (by language or project where set)
            </dd>
          </div>
          <div>
            <dt>Schedule</dt>
            <dd>
              On demand — Recompute all clients below
              {lastRun && (
                <small>
                  Last run: {monthLabel(lastRun.month)} ·{' '}
                  {fmtStamp(lastRun.computedAt)} by {lastRun.computedBy || '—'}
                </small>
              )}
            </dd>
          </div>
          <div>
            <dt>Coverage</dt>
            <dd>
              {fmt(voice.linkedClients)} client
              {voice.linkedClients === 1 ? '' : 's'} ·{' '}
              {fmt(voice.linkedOrganizations)} linked organization
              {voice.linkedOrganizations === 1 ? '' : 's'}
            </dd>
          </div>
        </dl>
      </div>
      <p className="muted-text billing-source-next">
        Next source: cognitive services spend (LLM, STT, TTS) from Kibana.
      </p>
    </Card>
  );
}

function MonthSummary({ month, run, linkedClients }) {
  const cards = summaryCards(run, linkedClients);
  return (
    <Card title={`Summary · ${monthLabel(month)}`}>
      {!cards ? (
        <p className="muted-text">
          No all-clients run for {monthLabel(month)} yet.
        </p>
      ) : (
        <div className="stats stats-five billing-summary">
          {cards.map(([label, value, sub, warn]) => (
            <div className={`stat${warn ? ' is-warn' : ''}`} key={label}>
              <span className="stat-label">{label}</span>
              <strong className="stat-value">{value}</strong>
              <span className="stat-sub">{sub}</span>
            </div>
          ))}
        </div>
      )}
      {run && (
        <p className="muted-text billing-rule">
          As computed by the all-clients run of {fmtStamp(run.computedAt)}.
          {run.recomputedSince > 0 &&
            ` ${run.recomputedSince} client${run.recomputedSince === 1 ? ' was' : 's were'} recomputed on their own since — Recompute all clients to bring this summary and the unlinked list up to date.`}
        </p>
      )}
    </Card>
  );
}

export function UnlinkedRow({ org, clients, onLinked, onClientCreated }) {
  const [clientId, setClientId] = useState(org.suggestion?.clientId || '');
  const [creating, setCreating] = useState(false);
  const [newClientName, setNewClientName] = useState(org.name || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(request) {
    setBusy(true);
    setError('');
    try {
      await request();
      await onLinked();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const link = () =>
    submit(() =>
      api(`/clients/${clientId}/platform-orgs/link`, {
        method: 'POST',
        body: JSON.stringify({ organizationId: org.organizationId }),
      }),
    );

  const createClient = () =>
    submit(async () => {
      await api(`/usage/platform-orgs/${org.organizationId}/client`, {
        method: 'POST',
        body: JSON.stringify({ name: newClientName.trim() }),
      });
      await onClientCreated();
    });

  return (
    <tr>
      <td>
        <b className="billing-org-name">
          {org.name || <span className="muted-text">Unknown name</span>}
          {org.deleted && (
            <span className="project-status-badge status-completed">
              Deleted on platform
            </span>
          )}
        </b>
        <code className="billing-org-id">{org.organizationId}</code>
      </td>
      <td
        className="billing-org-months"
        data-tooltip={org.months?.map(monthLabel).join(', ')}
      >
        {monthsSummary(org.months || [])}
      </td>
      <td className="num usage-calls">{fmt(org.calls)}</td>
      <td className="num usage-minutes">{fmt((org.seconds || 0) / 60)}</td>
      <td className="num usage-minutes">{fmt(org.billedMinutes)}</td>
      <td className="billing-link-cell">
        {org.linkedTo ? (
          <span className="muted-text">
            Linked to <b>{org.linkedTo.clientName}</b> · recompute{' '}
            {monthsSummary(org.months || [])} to include it
          </span>
        ) : (
          <div className="billing-link">
            {creating ? (
              <>
                <input
                  aria-label={`New client name for ${org.name || org.organizationId}`}
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  disabled={busy}
                />
                <button
                  type="button"
                  className="text-button"
                  onClick={() => {
                    setCreating(false);
                    setError('');
                  }}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="primary small"
                  onClick={createClient}
                  disabled={busy || newClientName.trim().length < 2}
                >
                  {busy ? 'Creating…' : 'Create & link'}
                </button>
              </>
            ) : (
              <>
                <Select
                  ariaLabel={`Client for ${org.name || org.organizationId}`}
                  value={clientId}
                  onChange={setClientId}
                  disabled={busy}
                  placeholder="Choose a client…"
                  searchable
                  options={clients.map((c) => ({ value: c.id, label: c.name }))}
                />
                <button
                  type="button"
                  className="secondary small"
                  onClick={link}
                  disabled={busy || !clientId}
                >
                  {busy ? 'Linking…' : 'Link'}
                </button>
                <button
                  type="button"
                  className="text-button"
                  onClick={() => {
                    setCreating(true);
                    setError('');
                  }}
                  disabled={busy}
                >
                  New client
                </button>
              </>
            )}
            {error && <div className="error">{error}</div>}
          </div>
        )}
      </td>
    </tr>
  );
}

export function Billing({ clients = [], onClientsChanged }) {
  const [state, setState] = useState({ runs: [], sources: {} });
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [pickedMonth, setMonth] = useSessionState(
    'billing.month',
    null,
    sanitizeMonth,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setState(await api('/usage/runs'));
      setLoaded(true);
      setLoadError('');
    } catch (err) {
      setLoadError(err.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function recomputeAll() {
    setBusy(true);
    setError('');
    try {
      await runUsageJob('/usage/runs', { month, source: 'voice_calls' });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const configured = state.sources.voice_calls?.configured;
  const sourceMonth = state.sources.voice_calls?.currentMonth;
  const month = pickedMonth ?? recentMonths(2, sourceMonth)[1];
  const unlinked = state.unlinked || [];
  const chartMonths = recentMonths(12, sourceMonth).reverse();
  const callsByMonth = monthlySeries(
    state.runs,
    chartMonths,
    (r) => r.totals.calls,
  );
  const durationByMonth = monthlySeries(
    state.runs,
    chartMonths,
    (r) => r.totals.seconds / 60,
  );
  const run = state.runs.find(
    (r) => r.month === month && r.source === 'voice_calls',
  );

  return (
    <div className="billing-page">
      <PageHeader
        title="Billing"
        subtitle="Platform usage collected for every client, month by month"
      />

      {loadError && (
        <div className="error">
          Couldn’t load billing runs: {loadError}{' '}
          <button type="button" className="secondary small" onClick={load}>
            Retry
          </button>
        </div>
      )}

      {loaded && !configured && (
        <div className="calendar-connect-hint warning">
          <AlertTriangle size={16} />
          <div>
            The voice call data source isn’t configured on this server, so
            months can’t be recomputed.
          </div>
        </div>
      )}

      <SourcesCard
        sources={state.sources}
        lastRun={state.sources.voice_calls?.lastRun}
      />

      <Card title="Recompute all clients">
        <div className="usage-recompute">
          <label>
            <span>Month</span>
            <Select
              ariaLabel="Month"
              value={month}
              onChange={setMonth}
              disabled={busy}
              options={monthOptions(month, 12, sourceMonth).map((m) => ({
                value: m,
                label: `${monthLabel(m)}${
                  m === thisMonth(sourceMonth) ? ' (in progress)' : ''
                }`,
              }))}
            />
          </label>
          <button
            type="button"
            className="secondary"
            onClick={recomputeAll}
            disabled={busy || !configured}
          >
            <RefreshCw size={14} />{' '}
            {busy ? 'Recomputing…' : 'Recompute all clients'}
          </button>
          {error && <div className="error">{error}</div>}
        </div>
      </Card>

      <MonthSummary
        month={month}
        run={run}
        linkedClients={state.sources.voice_calls?.linkedClients}
      />

      <Card title="Last 12 months · all clients">
        <div className="monthly-charts">
          <MonthlyBarChart title="Calls" unit="calls" points={callsByMonth} />
          <MonthlyBarChart
            title="Duration (min)"
            unit="min"
            points={durationByMonth}
          />
        </div>
        <p className="muted-text billing-rule">
          From each month’s all-clients run. Lighter bars are months computed
          before they ended; empty months were never computed.
        </p>
      </Card>

      <Card title="Runs" className="billing-runs">
        {state.runs.length === 0 ? (
          <p className="muted-text">No all-clients run yet.</p>
        ) : (
          <Table
            headers={[
              'Month',
              'Computed',
              'By',
              'Clients with calls',
              'Calls',
              'Duration (min)',
              'Tarification (min)',
              'Unlinked organizations',
            ]}
          >
            {state.runs.map((r) => (
              <tr
                key={`${r.month}|${r.source}`}
                className={r.month === month ? 'is-selected' : undefined}
              >
                <td className="strong">
                  <button
                    type="button"
                    className="billing-run-month"
                    onClick={() => setMonth(r.month)}
                  >
                    {monthLabel(r.month)}
                  </button>
                  {!r.complete && (
                    <span className="usage-partial">Partial</span>
                  )}
                </td>
                <td>{fmtStamp(r.computedAt)}</td>
                <td>{r.computedBy || '—'}</td>
                <td className="num">{fmt(r.clients)}</td>
                <td className="num usage-calls">{fmt(r.totals.calls)}</td>
                <td className="num usage-minutes">
                  {fmt(r.totals.seconds / 60)}
                </td>
                <td className="num usage-minutes">
                  {fmt(r.totals.billedMinutes)}
                </td>
                <td className="num">{fmt(r.unmapped.length)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <section className="card">
        <div className="card-head">
          <h2>Organizations with calls but no client</h2>
        </div>
        <p className="section-subtitle billing-unlinked-note">
          Across every computed month. Their calls are in no client’s
          Transaction data. Names come from the platform; a client with the same
          name is preselected — check it, link, then recompute the listed
          months.
        </p>
        {state.runs.length === 0 ? (
          <p className="muted-text">No all-clients run yet.</p>
        ) : unlinked.length === 0 ? (
          <p className="muted-text">
            Every organization with calls is linked to a client.
          </p>
        ) : (
          <div className="billing-unlinked">
            <Table
              headers={[
                'Organization',
                'Months',
                'Calls',
                'Duration (min)',
                'Tarification (min)',
                'Client',
              ]}
            >
              {unlinked.map((o) => (
                <UnlinkedRow
                  key={o.organizationId}
                  org={o}
                  clients={clients}
                  onLinked={load}
                  onClientCreated={onClientsChanged}
                />
              ))}
            </Table>
          </div>
        )}
      </section>
    </div>
  );
}
